import {
  Callout,
  Intent,
  PanelProps,
  Spinner,
  SpinnerSize,
} from '@blueprintjs/core';
import * as React from 'react';
import { GenericProperty } from 'vet-bones/components';
import { isEqual } from 'vet-bones/utils';

import { addDataSource } from 'src/actions/data-sources/action-creators/addDataSource';
import {
  DataSourceOption,
  DataSourceType,
  JDBC_DRIVER_KEY,
  STANDARD_JDBC_PROPERTY_KEYS,
} from 'src/constants/dataSourceTypeDescription';
import { DataSourceCreationForm } from 'src/containers/DataSourceCreation/DataSourceCreationForm';
import { DataSourcePanelContainer } from 'src/containers/ProjectResourceDialog/DataSourcePanelContainer';
import { useValidateDataSourceName } from 'src/containers/ProjectResourceDialog/ProjectFileUploadCreationPanel/hooks';
import {
  ProjectVirtualGraphCreationPanel,
  ProjectVirtualGraphCreationPanelProps,
} from 'src/containers/ProjectResourceDialog/ProjectVirtualGraphCreationPanel/ProjectVirtualGraphCreationPanel';
import { ProjectResourceDialogPanelProps } from 'src/containers/ProjectResourceDialog/types';
import { connectionSelector } from 'src/selectors/connection';
import { dataSourcePropertiesSelector } from 'src/selectors/dataSourceProperties';
import { SqlDataSource } from 'src/store/state/dataSources';
import * as copy from 'src/templates/copy';
import {
  countUniqueExistingProperties,
  isRowInvalid,
} from 'src/utils/genericPropertyValidation';
import { useIsMounted } from 'src/utils/hooks/useIsMounted';
import { useReduxDispatch } from 'src/utils/hooks/useReduxDispatch';
import { useReduxSelector } from 'src/utils/hooks/useReduxSelector';

export type DataSourceCreationPanelProps = ProjectResourceDialogPanelProps & {
  type: DataSourceType;
};

export const DataSourceCreationPanel: React.VFC<
  PanelProps<DataSourceCreationPanelProps>
> = ({ closePanel, openPanel, setFooterProps, type }) => {
  const isMounted = useIsMounted();

  const connection = useReduxSelector(connectionSelector);
  const dispatch = useReduxDispatch();
  const isPendingDataSourceCreation = useReduxSelector(
    (state) => state.dataSources.pendingAddDataSource
  );
  const propertyDescriptions = useReduxSelector(
    dataSourcePropertiesSelector(connection, type),
    isEqual
  );

  const [dataSourceName, setDataSourceName] = React.useState<string>('');
  const [standardProperties, setStandardProperties] = React.useState<
    Record<string, string>
  >(
    Array.from(STANDARD_JDBC_PROPERTY_KEYS).reduce(
      (agg, property) => ({
        ...agg,
        [property]: propertyDescriptions[property]?.default || '',
      }),
      {}
    )
  );
  const [advancedProperties, setAdvancedProperties] = React.useState<
    GenericProperty[]
  >([]);

  const validateName = useValidateDataSourceName();

  const dataSourceNameError = validateName(dataSourceName);

  const {
    invalidDataSourcePropertyNames,
    missingDataSourcePropertyNames,
    newDataSourceOptions,
  } = React.useMemo(() => {
    const invalidDataSourcePropertyNames = new Set<string>();
    const missingDataSourcePropertyNames = new Set<string>();
    const newDataSourceOptions: Record<string | string, any> = {
      // not "required" for creation, but sets type on Stardog (otherwise Generic)
      [JDBC_DRIVER_KEY]: propertyDescriptions?.[JDBC_DRIVER_KEY]?.default,
    };
    Object.entries(DataSourceOption).forEach(([propertyNameArg, property]) => {
      const propertyName = propertyNameArg as keyof typeof DataSourceOption;

      newDataSourceOptions[property] = standardProperties[property];
      if (
        propertyDescriptions[property]?.required &&
        !newDataSourceOptions[property]
      ) {
        missingDataSourcePropertyNames.add(
          copy.components.projectResourceDialog.projectDataSourceCreationPanel
            .propertyLabels[propertyName]
        );
      }
    });
    const existingPropertyCounts =
      countUniqueExistingProperties(advancedProperties);
    advancedProperties.forEach(({ name, value }) => {
      newDataSourceOptions[name] = value;
      if (
        isRowInvalid(
          name,
          value,
          STANDARD_JDBC_PROPERTY_KEYS,
          existingPropertyCounts
        )
      ) {
        invalidDataSourcePropertyNames.add(name);
      }
    });
    return {
      invalidDataSourcePropertyNames,
      missingDataSourcePropertyNames,
      newDataSourceOptions,
    };
  }, [propertyDescriptions, advancedProperties, standardProperties]);

  const handleCreateButtonClick = React.useCallback(async () => {
    if (!connection) {
      return;
    }

    const newDataSource: SqlDataSource = {
      entityName: '',
      available: true,
      sharable: true,
      id: dataSourceName,
      type,
      options: newDataSourceOptions as SqlDataSource['options'],
      tables: [],
      tableMetadataErrorResponse: null,
    };

    const didSucceed = await dispatch(addDataSource(newDataSource));

    if (!didSucceed) {
      return;
    }

    const props: ProjectVirtualGraphCreationPanelProps = {
      setFooterProps,
      dataSourceId: newDataSource.id,
    };
    closePanel();
    openPanel({
      renderPanel: ProjectVirtualGraphCreationPanel,
      props,
    });
  }, [
    closePanel,
    connection,
    dataSourceName,
    dispatch,
    newDataSourceOptions,
    openPanel,
    setFooterProps,
    type,
  ]);

  React.useEffect(() => {
    if (isMounted.current) {
      let createButtonsError = '';
      if (dataSourceNameError) {
        createButtonsError = dataSourceNameError;
      } else if (invalidDataSourcePropertyNames.size) {
        createButtonsError =
          copy.components.projectResourceDialog.projectDataSourceCreationPanel.invalidDataSourceProperties(
            Array.from(invalidDataSourcePropertyNames.values())
          );
      } else if (missingDataSourcePropertyNames.size) {
        createButtonsError =
          copy.components.projectResourceDialog.projectDataSourceCreationPanel.missingDataSourceProperties(
            Array.from(missingDataSourcePropertyNames.values())
          );
      }

      setFooterProps({
        createButtonsError,
        handleBackButtonClick: closePanel,
        handleCreateButtonClick,
      });
    }
  }, [
    closePanel,
    dataSourceNameError,
    handleCreateButtonClick,
    invalidDataSourcePropertyNames,
    isMounted,
    missingDataSourcePropertyNames,
    setFooterProps,
  ]);

  if (isPendingDataSourceCreation) {
    return (
      <Callout className="sd-data-source-panel" intent={Intent.NONE}>
        <Spinner size={SpinnerSize.LARGE} />;
      </Callout>
    );
  }

  return (
    <DataSourcePanelContainer connectionType={type}>
      <DataSourceCreationForm
        advancedProperties={advancedProperties}
        dataSourceName={dataSourceName}
        onUpdateAdvancedProperties={setAdvancedProperties}
        onUpdateName={setDataSourceName}
        onUpdateStandardProperties={setStandardProperties}
        standardProperties={standardProperties}
        type={type}
      />
    </DataSourcePanelContainer>
  );
};
